import React, { useState, useEffect } from "react";
import { Spin, Col, Row, Button, Input, Select, Modal } from "antd";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Trash2,
  Search,
  FileText,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import {
  ChatsTrashTable,
  SuccessNotification,
  ErrorNotification,
} from "@components";
import { getAllAdminChatArchivesPaginated } from "@api/user/admin";
import { deleteMultipleChats } from "@api/copilots/chat";
import { useTranslation } from "react-i18next";
import "@assets/css/History.css";

const { Option } = Select;
const { Search: SearchInput } = Input;

const ChatTrashList = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [chats, setChats] = useState([]);
  const [totalChats, setTotalChats] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    setFilteredData(chats);
  }, [chats]);

  useEffect(() => {
    handleFetchArchivedChatsPaginated(page);
  }, []);

  // Fetch history paginated
  const handleFetchArchivedChatsPaginated = async (page) => {
    setLoading(true);
    getAllAdminChatArchivesPaginated(page, perPage)
      .then((res) => {
        setChats(res.data.chats);
        setPage(res.data.paginationData.page);
        setPerPage(res.data.paginationData.perPage);
        setTotalChats(res.data.paginationData.totalChats);
        setTotalPages(res.data.paginationData.totalPages);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Fetch all chats error", err);
      });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(1);
    setPerPage(10);
    setLoading(true);

    getAllAdminChatArchivesPaginated(page, perPage, value)
      .then((res) => {
        setChats(res.data.chats);
        setTotalChats(res.data.paginationData.totalChats);
        setTotalPages(res.data.paginationData.totalPages);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Fetch all chats error", err);
      });
  };

  const handleSort = (value) => {
    const sortedData = [...filteredData];

    if (value === "date_desc") {
      sortedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (value === "date_asc") {
      sortedData.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }

    setFilteredData(sortedData);
  };

  const handleRowClick = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };

  const handleDeleteMultipleChats = async (ids) => {
    Modal.confirm({
      title: "Are you sure you want to permanently delete selected chats?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          await deleteMultipleChats(ids);
          SuccessNotification("Chats deleted successfully");
          handleFetchArchivedChatsPaginated(page);
        } catch (err) {
          ErrorNotification("Error deleting chats");
        } finally {
          setSelectedIds([]);
          setLoading(false);
        }
      },
    });
  };

  return (
    <div className="history-container">
      {/* Main Content */}
      <div className="history-content">
        {/* Header Section */}
        <div className="history-header">
          <div className="history-header-left">
            <div className="history-icon" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)" }}>
              <Trash2 size={20} color="#fff" />
            </div>
            <div>
              <h1 className="history-title">
                {t("chatTrash.chatTrash")}
              </h1>
              <p className="history-subtitle">
                Archived chats ready for permanent deletion
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col xs={24} md={14}>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Button
                onClick={() =>
                  history.push(`/a/ws/${user.workspaceId}/trash/chats`)
                }
                className="history-tab-btn active"
                style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", borderColor: "transparent" }}
                icon={<MessageSquare size={16} />}
              >
                {t("chatTrash.chatsTrash")}
              </Button>

              <Button
                onClick={() =>
                  history.push(`/a/ws/${user.workspaceId}/trash/generations`)
                }
                className="history-tab-btn"
                icon={<FileText size={16} />}
              >
                {t("chatTrash.generationsTrash")}
              </Button>

              {selectedIds.length > 0 && (
                <Button
                  onClick={() => handleDeleteMultipleChats(selectedIds)}
                  className="history-archive-btn"
                  icon={<AlertTriangle size={16} />}
                >
                  ({selectedIds.length}) {t("chatTrash.deleteSelected")}
                </Button>
              )}
            </div>
          </Col>

          <Col xs={24} md={10}>
            <div style={{ display: "flex", gap: "10px" }}>
              <SearchInput
                placeholder="Search chat name"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className="history-search-input"
                prefix={<Search size={16} style={{ color: "#8B5CF6" }} />}
              />

              <Select
                defaultValue="date_desc"
                onChange={handleSort}
                className="history-sort-select"
                dropdownClassName="history-dropdown"
              >
                <Option value="date_desc">
                  {t("chatTrash.dateNewToOld")}
                </Option>
                <Option value="date_asc">
                  {t("chatTrash.dateOldToNew")}
                </Option>
              </Select>
            </div>
          </Col>
        </Row>

        {/* Table Section */}
        {loading ? (
          <div className="history-loading">
            <Spin size="large" />
            <span>Loading archived chats...</span>
          </div>
        ) : (
          <div className="history-table-container">
            <ChatsTrashTable
              page={page}
              setPage={setPage}
              perPage={perPage}
              totalChats={totalChats}
              totalPages={totalPages}
              chats={filteredData}
              handleFetchChatsHistoryPaginated={
                handleFetchArchivedChatsPaginated
              }
              expandedRow={expandedRow}
              handleRowClick={handleRowClick}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatTrashList;
